import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Div from '@hellodarwin/core/lib/components/common/div';
import { useCallback, useMemo, useState, } from 'react';
import OverflowTooltip from '../overflow-tooltip';
import EditableSortedTableCell from './editable-cell';
import { SortedTableBase, SortedTableSorter, } from './styles';
export { SortedTableBase, SortedTableSorter };
const EMPTY_ROW_KEY = '__sorted_table_empty_row__';
const getColumnWidth = (column) => { var _a; return (_a = column.width) !== null && _a !== void 0 ? _a : column.maxWidth; };
const getConstrainedColumnStyle = (column) => {
    const width = getColumnWidth(column);
    const style = {};
    if (width !== undefined) {
        style.width = width;
    }
    if (column.minWidth !== undefined) {
        style.minWidth = column.minWidth;
    }
    if (column.maxWidth !== undefined) {
        style.maxWidth = column.maxWidth;
    }
    return Object.keys(style).length ? style : undefined;
};
const SortedTable = ({ columns, rows, maxHeight, minTableWidth, naturalHeight, horizontalScroll, loading, emptyLabel, disableVirtual, virtual, compactRows, onScroll, sortColumnKey, sortDirection: controlledSortDirection, onSortChange, }) => {
    const [sortIndex, setSortIndex] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const usesControlledSort = !!onSortChange;
    const activeSortIndex = usesControlledSort
        ? columns.findIndex((column) => column.key === sortColumnKey)
        : sortIndex;
    const activeSortDirection = controlledSortDirection !== null && controlledSortDirection !== void 0 ? controlledSortDirection : sortDirection;
    const hasHorizontalScroll = horizontalScroll || !!minTableWidth;
    const tableScroll = hasHorizontalScroll || !naturalHeight
        ? Object.assign(Object.assign({}, (horizontalScroll
            ? { x: 'max-content' }
            : minTableWidth
                ? { x: minTableWidth }
                : {})), (!naturalHeight ? { y: maxHeight !== null && maxHeight !== void 0 ? maxHeight : 320 } : {})) : undefined;
    const handleCellClick = useCallback((event, rowClick) => {
        const target = event.target;
        if (target === null || target === void 0 ? void 0 : target.closest('a, button, input, label, .sorted-table-clickable, .dashboard-clickable')) {
            return;
        }
        if (rowClick) {
            rowClick();
            return;
        }
        const currentTarget = event.currentTarget;
        const link = currentTarget.querySelector('a.sorted-table-link, a.dashboard-link');
        if (!link) {
            const clickable = currentTarget.querySelector('.sorted-table-clickable, .dashboard-clickable');
            if (clickable) {
                clickable.click();
            }
            return;
        }
        if (link.target === '_blank') {
            window.open(link.href, '_blank', 'noreferrer');
            return;
        }
        link.click();
    }, []);
    const sortedRows = useMemo(() => {
        if (usesControlledSort)
            return rows;
        if (sortIndex === null)
            return rows;
        const safeRows = rows.slice();
        safeRows.sort((a, b) => {
            var _a, _b;
            const valueA = (_a = a.sortValues) === null || _a === void 0 ? void 0 : _a[sortIndex];
            const valueB = (_b = b.sortValues) === null || _b === void 0 ? void 0 : _b[sortIndex];
            if (valueA == null && valueB == null)
                return 0;
            if (valueA == null)
                return 1;
            if (valueB == null)
                return -1;
            if (typeof valueA === 'number' && typeof valueB === 'number') {
                return activeSortDirection === 'asc'
                    ? valueA - valueB
                    : valueB - valueA;
            }
            const textA = String(valueA);
            const textB = String(valueB);
            const compare = textA.localeCompare(textB, undefined, {
                sensitivity: 'base',
                numeric: true,
            });
            if (compare === 0)
                return 0;
            return activeSortDirection === 'asc' ? compare : -compare;
        });
        return safeRows;
    }, [rows, sortIndex, activeSortDirection, usesControlledSort]);
    const rowClickByKey = useMemo(() => {
        return new Map(sortedRows.map((row) => [row.key, row.onClick]));
    }, [sortedRows]);
    const rowByKey = useMemo(() => {
        return new Map(sortedRows.map((row) => [row.key, row]));
    }, [sortedRows]);
    const tableColumns = useMemo(() => {
        return columns.map((column, columnIndex) => {
            const isSortable = column.sortable !== false;
            const isActive = activeSortIndex === columnIndex;
            const columnWidth = getColumnWidth(column);
            const constrainedColumnStyle = getConstrainedColumnStyle(column);
            return {
                key: column.key,
                dataIndex: column.key,
                align: column.align,
                width: columnWidth,
                fixed: column.fixed,
                onCell: (record) => {
                    const rowClick = typeof record.key === 'string'
                        ? rowClickByKey.get(record.key)
                        : undefined;
                    const fullWidth = record.__sortedTableFullWidth === true;
                    return {
                        colSpan: fullWidth ? (columnIndex === 0 ? columns.length : 0) : 1,
                        style: fullWidth ? undefined : constrainedColumnStyle,
                        onClick: (event) => handleCellClick(event, rowClick),
                    };
                },
                onHeaderCell: () => ({
                    style: constrainedColumnStyle,
                }),
                title: (_jsxs(Div, { flex: "row", align: "center", gap: 6, style: {
                        justifyContent: column.align === 'center'
                            ? 'center'
                            : column.align === 'right'
                                ? 'flex-end'
                                : 'flex-start',
                        cursor: isSortable ? 'pointer' : 'default',
                        userSelect: 'none',
                    }, onClick: () => {
                        if (!isSortable)
                            return;
                        if (usesControlledSort) {
                            if (isActive) {
                                onSortChange(column.key, activeSortDirection === 'asc' ? 'desc' : 'asc');
                            }
                            else {
                                onSortChange(column.key, 'asc');
                            }
                            return;
                        }
                        if (sortIndex === columnIndex) {
                            if (activeSortDirection === 'asc') {
                                setSortDirection('desc');
                            }
                            else {
                                setSortIndex(null);
                                setSortDirection('asc');
                            }
                        }
                        else {
                            setSortIndex(columnIndex);
                            setSortDirection('asc');
                        }
                    }, children: [_jsx("span", { children: column.label }), isSortable && (_jsx(SortedTableSorter, { "$active": isActive, "$direction": activeSortDirection }))] })),
                render: (value, record) => {
                    var _a, _b, _c;
                    const cellAlign = record.key === EMPTY_ROW_KEY || record.__sortedTableFullWidth === true
                        ? 'left'
                        : column.align || 'left';
                    const tooltipTitle = Array.isArray(record.__sortedTableTooltipValues)
                        ? record.__sortedTableTooltipValues[columnIndex]
                        : undefined;
                    const content = value;
                    const shouldRenderOverflowTooltip = column.overflowTooltip &&
                        record.key !== EMPTY_ROW_KEY &&
                        record.__sortedTableFullWidth !== true;
                    const row = typeof record.key === 'string'
                        ? rowByKey.get(record.key)
                        : undefined;
                    const displayContent = shouldRenderOverflowTooltip ? (_jsx(OverflowTooltip, { title: tooltipTitle !== null && tooltipTitle !== void 0 ? tooltipTitle : content, className: "sorted-table-overflow-tooltip", children: content })) : (content);
                    const editableContent = row &&
                        column.editable &&
                        record.key !== EMPTY_ROW_KEY &&
                        record.__sortedTableFullWidth !== true &&
                        ((_c = (_b = (_a = column.editable).isEditable) === null || _b === void 0 ? void 0 : _b.call(_a, row)) !== null && _c !== void 0 ? _c : true)
                        ? (_jsx(EditableSortedTableCell, { row: row, editableConfig: column.editable, children: displayContent }))
                        : displayContent;
                    return (_jsx("div", { className: `sorted-table-cell-inner sorted-table-cell-inner-${cellAlign}`, children: editableContent }));
                },
            };
        });
    }, [
        columns,
        handleCellClick,
        rowClickByKey,
        activeSortDirection,
        activeSortIndex,
        onSortChange,
        sortIndex,
        usesControlledSort,
        rowByKey,
    ]);
    const dataSource = useMemo(() => {
        if (loading)
            return [];
        if (!sortedRows.length) {
            const emptyRecord = { key: EMPTY_ROW_KEY };
            columns.forEach((column, index) => {
                emptyRecord[column.key] =
                    index === 0 ? (_jsx("span", { className: "sorted-table-empty-label", children: emptyLabel })) : null;
            });
            return [emptyRecord];
        }
        return sortedRows.map((row) => {
            const record = { key: row.key };
            if (row.fullWidth) {
                record.__sortedTableFullWidth = true;
                record.__sortedTableFullWidthTone = row.fullWidthTone;
            }
            if (row.tooltipValues) {
                record.__sortedTableTooltipValues = row.tooltipValues;
            }
            columns.forEach((column, index) => {
                var _a;
                record[column.key] = (_a = row.cells[index]) !== null && _a !== void 0 ? _a : null;
            });
            return record;
        });
    }, [columns, emptyLabel, loading, sortedRows]);
    return (_jsx(SortedTableBase, { "$compact": compactRows, "$horizontalScroll": hasHorizontalScroll, "$naturalHeight": naturalHeight, columns: tableColumns, dataSource: dataSource, pagination: false, loading: loading, onScroll: onScroll, rowClassName: (record) => {
            if (record.key === EMPTY_ROW_KEY)
                return 'sorted-table-empty-row';
            if (record.__sortedTableFullWidth === true) {
                return [
                    'sorted-table-full-width-row',
                    record.__sortedTableFullWidthTone === 'danger'
                        ? 'sorted-table-full-width-row-danger'
                        : '',
                ]
                    .filter(Boolean)
                    .join(' ');
            }
            return typeof record.key === 'string' && rowClickByKey.get(record.key)
                ? 'sorted-table-row-clickable'
                : '';
        }, virtual: disableVirtual ? false : (virtual !== null && virtual !== void 0 ? virtual : false), scroll: tableScroll, tableLayout: horizontalScroll ? 'auto' : 'fixed', locale: {
            emptyText: loading ? null : emptyLabel,
        } }));
};
export default SortedTable;
